<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
interface Props {
  modelValue?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: 'text' | 'password' | 'email' | 'tel' | 'number' | 'search' | 'url';
  maxlength?: number;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  placeholder: '',
  disabled: false,
  type: 'text',
  maxlength: undefined,
});
const emit = defineEmits<{
  'update:modelValue': [value: string];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();
const inputRef = useTemplateRef<HTMLInputElement>('input');
const hasValue = computed(() => !!props.modelValue);
function focus() {
  inputRef.value?.focus();
}
defineExpose({ focus });
</script>

<template>
  <div :class="$style.root">
    <div v-if="props.label" :class="$style.label">{{ props.label }}</div>
    <div
      :class="[$style.wrapper, { [$style.hasValue]: hasValue, [$style.disabled]: props.disabled }]"
      @click="focus"
    >
      <input
        ref="input"
        :value="props.modelValue"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :type="props.type"
        :maxlength="props.maxlength"
        :class="$style.input"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '@/ui-kit/styles/typography';

.root {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.label {
  font-family: typography.$font-family-primary;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  font-style: normal;
  color: var(--ui2-content-secondary, #655e60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wrapper {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
  border: 1.5px solid transparent;
  background: var(--ui2-shade-black-4, rgba(0, 0, 0, 0.04));
  box-sizing: border-box;
  cursor: text;

  &:hover {
    background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));
  }

  &.hasValue {
    background: var(--ui2-shade-black-8, rgba(0, 0, 0, 0.08));

    &:hover {
      background: var(--ui2-shade-black-12, rgba(0, 0, 0, 0.12));
    }
  }

  &:focus-within {
    background: var(--ui2-background-primary, #fff);
    border-color: var(--ui2-state-focus, rgba(51, 85, 255, 1));

    &:hover,
    &.hasValue:hover {
      background: var(--ui2-background-primary, #fff);
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.48;
    pointer-events: none;
  }
}

.input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  background: none;
  outline: none;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  font-style: normal;
  font-weight: 400;
  color: var(--ui2-content-primary, #010000);
  cursor: inherit;

  &::placeholder {
    color: var(--ui2-content-tertiary, #978f91);
  }

  &:disabled {
    cursor: not-allowed;
  }
}
</style>
